import { z } from "zod";
export const ceilings = {
  inputBytes: 10000000,
  expandedBytes: 50000000,
  entries: 2000,
  entryBytes: 20000000,
  compressionRatio: 200,
  sheets: 20,
  rows: 10000,
  columns: 500,
  cells: 100000,
  cellChars: 32768,
  diagnostics: 1000,
  previewRows: 10000,
  parseMs: 5000,
};
const limitSchema = z
  .object(
    Object.fromEntries(
      Object.entries(ceilings).map(([key, max]) => [
        key,
        z.number().int().min(1).max(max).optional(),
      ]),
    ) as Record<keyof typeof ceilings, z.ZodOptional<z.ZodNumber>>,
  )
  .strict();
export const parseOptionsSchema = z
  .object({
    version: z.literal(1).default(1),
    format: z.enum(["auto", "csv", "delimited", "xlsx"]).default("auto"),
    encoding: z
      .enum(["auto", "utf-8", "utf-16le", "utf-16be", "windows-1252"])
      .default("auto"),
    delimiter: z
      .string()
      .length(1)
      .refine((v) => !/[\r\n"\0]/.test(v), "Invalid delimiter")
      .optional(),
    headerRow: z
      .union([z.literal("auto"), z.number().int().min(1).max(1048576)])
      .default("auto"),
    previewRows: z.number().int().min(1).max(10000).default(100),
    limits: limitSchema.default({}),
  })
  .strict();
export type ParseOptions = z.input<typeof parseOptionsSchema>;
export interface Location {
  sheet: number;
  row: number;
  column: number;
  address: string;
}
export interface Cell {
  value: string;
  location: Location;
}
export interface Diagnostic {
  code: string;
  message: string;
  location?: Location;
}
export interface Sheet {
  name: string;
  headers: string[];
  headerCells: Cell[];
  rows: string[][];
  cells: Cell[][];
  sourceRows: number[];
  headerRow: number;
  rowCount: number;
  columnCount: number;
  dataRange: {
    firstRow: number;
    lastRow: number;
    firstColumn: number;
    lastColumn: number;
  } | null;
  truncated: boolean;
}
export interface TabularResult {
  version: 1;
  format: "csv" | "delimited" | "xlsx";
  encoding: "utf-8" | "utf-16le" | "utf-16be" | "windows-1252" | "xlsx";
  delimiter: string;
  sheets: Sheet[];
  diagnostics: Diagnostic[];
}
export class TabularError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}
export interface Runtime {
  signal?: AbortSignal;
  now?: () => number;
}
export function address(row: number, column: number) {
  let letters = "";
  for (let n = column; n; n = Math.floor((n - 1) / 26))
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
  return `${letters}${row}`;
}
export class Budget {
  readonly limits: typeof ceilings;
  readonly started: number;
  cells = 0;
  diagnostics: Diagnostic[] = [];
  constructor(
    readonly options: z.output<typeof parseOptionsSchema>,
    readonly runtime: Runtime = {},
  ) {
    this.limits = {
      ...ceilings,
      ...Object.fromEntries(
        Object.entries(options.limits).filter(([, v]) => v !== undefined),
      ),
    };
    this.limits.previewRows = Math.min(
      options.previewRows,
      this.limits.previewRows,
    );
    this.started = (runtime.now || Date.now)();
  }
  check() {
    if (this.runtime.signal?.aborted)
      throw new TabularError("CANCELLED", "Tabular import was cancelled");
    if ((this.runtime.now || Date.now)() - this.started > this.limits.parseMs)
      throw new TabularError(
        "TIME_LIMIT",
        "Tabular import exceeded its time budget",
      );
  }
  text(value: unknown) {
    const s = String(value ?? "");
    if (s.includes("\0"))
      throw new TabularError(
        "NULL_CHARACTER",
        "Null characters are not allowed",
      );
    if (s.length > this.limits.cellChars)
      throw new TabularError("CELL_LIMIT", "Cell text exceeds its limit");
    return s;
  }
  cell(value: unknown, sheet: number, row: number, column: number): Cell {
    this.check();
    if (++this.cells > this.limits.cells)
      throw new TabularError("CELL_COUNT", "Import exceeds its cell budget");
    if (column > this.limits.columns || row > 1048576)
      throw new TabularError(
        "RANGE_LIMIT",
        "Cell coordinates exceed their bounds",
      );
    return {
      value: this.text(value),
      location: { sheet, row, column, address: address(row, column) },
    };
  }
  warn(code: string, message: string, location?: Location) {
    if (this.diagnostics.length >= this.limits.diagnostics)
      throw new TabularError(
        "DIAGNOSTIC_LIMIT",
        "Import exceeds its diagnostic budget",
      );
    this.diagnostics.push({ code, message, location });
  }
}
export function makeSheet(
  name: string,
  index: number,
  records: { row: number; cells: Cell[] }[],
  budget: Budget,
  declaredColumns = 0,
): Sheet {
  const options = budget.options,
    header =
      options.headerRow === "auto"
        ? records.find((r) => r.cells.some((c) => c.value.trim()))?.row
        : options.headerRow;
  if (!header)
    throw new TabularError("NO_HEADER", "No nonempty header row was found");
  const headerRecord = records.find((r) => r.row === header);
  if (!headerRecord)
    throw new TabularError(
      "NO_HEADER",
      "Selected header row is empty or missing",
    );
  const data = records.filter(
    (r) => r.row > header && r.cells.some((c) => c.value !== ""),
  );
  if (data.length > budget.limits.rows)
    throw new TabularError("ROW_LIMIT", "Sheet exceeds its row limit");
  const columnCount = records
    .filter((r) => r.row >= header)
    .reduce((n, r) => Math.max(n, r.cells.length), declaredColumns);
  if (!columnCount || columnCount > budget.limits.columns)
    throw new TabularError("COLUMN_LIMIT", "Sheet exceeds its column limit");
  const headerCells = Array.from(
      { length: columnCount },
      (_, i) =>
        headerRecord.cells[i] || {
          value: "",
          location: {
            sheet: index,
            row: header,
            column: i + 1,
            address: address(header, i + 1),
          },
        },
    ),
    headers: string[] = [],
    used = new Set<string>();
  headerCells.forEach((cell, i) => {
    const base = cell.value.trim() || `column_${i + 1}`;
    let candidate = base,
      n = 1;
    while (used.has(candidate.normalize("NFKC").toLowerCase())) {
      const suffix = `_${++n}`;
      candidate =
        base.slice(0, Math.max(0, budget.limits.cellChars - suffix.length)) +
        suffix;
    }
    used.add(candidate.normalize("NFKC").toLowerCase());
    headers.push(candidate);
  });
  const retained = data.slice(0, budget.limits.previewRows),
    cells = retained.map((r) =>
      Array.from(
        { length: columnCount },
        (_, i) =>
          r.cells[i] || {
            value: "",
            location: {
              sheet: index,
              row: r.row,
              column: i + 1,
              address: address(r.row, i + 1),
            },
          },
      ),
    );
  return {
    name: budget.text(name),
    headers,
    headerCells,
    cells,
    rows: cells.map((r) => r.map((c) => c.value)),
    sourceRows: retained.map((r) => r.row),
    headerRow: header,
    rowCount: data.length,
    columnCount,
    dataRange: data.length
      ? {
          firstRow: data[0].row,
          lastRow: data.at(-1)!.row,
          firstColumn: 1,
          lastColumn: columnCount,
        }
      : null,
    truncated: retained.length < data.length,
  };
}
